import { AA_TEXT, contrastRatio, luminance, parseHex, SURFACES } from './contrast'
import { mixOklab } from './oklab'

/**
 * Surfaces où un TEXTE de marque se pose, par thème : fond de page puis
 * cartes et zones atténuées. Miroir des jetons `background`, `card` et `muted`
 * de `apps/web/src/index.css`.
 */
export const TEXT_SURFACES = {
  light: [SURFACES.light, '#FFFFFF', '#F3ECF7'],
  dark: [SURFACES.dark, '#2A2131'],
} as const

/** Pas du glissement vers le noir ou le blanc : assez fin pour rester proche de la marque. */
const STEP = 0.05

/**
 * Teinte de `color` lisible comme texte sur toutes les `surfaces` (RGAA 3.2).
 *
 * La couleur de marque reste la couleur des boutons dans les deux thèmes ; mais
 * posée telle quelle en TEXTE, une couleur lisible sur fond clair ne l'est plus
 * sur fond sombre, et inversement (un bleu nuit tombe sous 2:1 en sombre). On
 * garde donc la teinte et on la glisse en oklab vers le noir (surfaces claires)
 * ou le blanc (surfaces sombres), juste assez pour atteindre 4,5:1.
 *
 * Rend la couleur inchangée si elle est déjà lisible, `null` si elle est illisible.
 */
export function readableTextOn(color: string, surfaces: readonly string[]): string | null {
  const rgb = parseHex(color)
  const firstSurface = surfaces[0] ? parseHex(surfaces[0]) : null
  if (!rgb || !firstSurface) return null

  const readable = (candidate: string) =>
    surfaces.every(surface => (contrastRatio(candidate, surface) ?? 0) >= AA_TEXT)
  const hex = `#${rgb.map(c => c.toString(16).padStart(2, '0')).join('')}`.toUpperCase()
  if (readable(hex)) return hex

  const extreme = luminance(firstSurface) > 0.5 ? '#000000' : '#FFFFFF'
  for (let share = 1 - STEP; share > 0; share -= STEP) {
    const candidate = mixOklab(hex, extreme, share)
    if (candidate && readable(candidate)) return candidate
  }
  return extreme
}
